import React, { useState, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { AlertTriangle } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { setIsItemInfoModalOpen, setItemInfoModalData } from '../../../../redux/modal/modal.actions';
import { getLocalizedName } from '../../../../utility/util/util.service';

const AutocompleteField = forwardRef(({
                                         className,
                                         suggestions,
                                         placeholder,
                                         autoFocus,
                                         clearInput,
                                         externalClick,
                                         onChange,
                                         onKeyDown,
                                         onBlur,
                                         onSuggestionsShown,
                                         onSuggestionItemClick,
                                         value,
                                       }, ref) => {
  const history = useHistory();
  const intl = useIntl();
  const dispatch = useDispatch();
  const store = useSelector(state => state.store.store);

  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userInput, setUserInput] = useState(value || '');
  const [focused, setFocused] = useState(false);
  const [openUp, setOpenUp] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionListRef = useRef(null);

  const filteredData = useRef([]);

  // Sync with external value
  useEffect(() => {
    setUserInput(value || '');
  }, [value]);

  // Handle external clicks to close suggestions
  useEffect(() => {
    const handleExternalClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        if (externalClick) externalClick(e);
      }
    };

    document.body.addEventListener('click', handleExternalClick);
    return () => {
      document.body.removeEventListener('click', handleExternalClick);
    };
  }, [externalClick]);

  // Auto focus input if prop is set
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Call onSuggestionsShown callback when suggestions are shown
  useEffect(() => {
    if (onSuggestionsShown && showSuggestions) {
      onSuggestionsShown(userInput);
    }
  }, [showSuggestions, userInput, onSuggestionsShown]);

  const handleSuggestionClick = (suggestion, e) => {
    if (onSuggestionItemClick) {
      onSuggestionItemClick();
    }

    setActiveSuggestion(0);
    setShowSuggestions(false);
    setUserInput(e.currentTarget.innerText);

    if (suggestion.type === 'FoodItemData') {
      dispatch(setItemInfoModalData({
        itemData: suggestion,
        categories: suggestion?.categories
      }));
      dispatch(setIsItemInfoModalOpen(true));
    } else {
      history.push(`/store/${store.slug}/${suggestion.id}`);
    }
  };

  const handleInputChange = (e) => {
    const value = e.currentTarget.value;
    setUserInput(value);
    setActiveSuggestion(0);
    setShowSuggestions(value.length > 0);

    if (onChange) {
      onChange(e);
    }
  };

  const handleInputKeyDown = (e) => {
    // User pressed the up arrow
    if (e.keyCode === 38 && activeSuggestion !== 0) {
      setActiveSuggestion(activeSuggestion - 1);
      if (suggestionListRef.current && activeSuggestion <= filteredData.current.length / 2) {
        suggestionListRef.current.scrollTop = 0;
      }
    }
    // User pressed the down arrow
    else if (e.keyCode === 40 && activeSuggestion < filteredData.current.length - 1) {
      setActiveSuggestion(activeSuggestion + 1);
      if (suggestionListRef.current && activeSuggestion >= filteredData.current.length / 2) {
        suggestionListRef.current.scrollTop = suggestionListRef.current.scrollHeight;
      }
    }
    // User Pressed ESC
    else if (e.keyCode === 27) {
      setShowSuggestions(false);
      setUserInput('');
    }
    // User Pressed ENTER
    else if (e.keyCode === 13 && showSuggestions && filteredData.current[activeSuggestion]) {
      const suggestion = filteredData.current[activeSuggestion];
      handleSuggestionClick(suggestion, e);
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const renderSuggestions = () => {
    filteredData.current = suggestions || [];

    return (
      <>
        {suggestions.length > 0 ? (
          <li className="suggestion-item suggestion-title text-primary text-bold-600">
            <p className="mb-0">
              <FormattedMessage id="searchResults" />
            </p>
          </li>
        ) : (
          <li className="suggestion-item no-result">
            <AlertTriangle size={15} />{' '}
            <span className="align-middle ml-50">
              <FormattedMessage id="searchNoResults" />
            </span>
          </li>
        )}
        {suggestions?.map((data, index) => (
          <div
            onClick={(e) => handleSuggestionClick(data, e)}
            key={data.id}
            className={classnames('suggestion-item d-block cursor-pointer', {
              active: index === activeSuggestion,
            })}
          >
            <p className="mb-0">{getLocalizedName(data, intl.locale)}</p>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="vx-autocomplete-container" ref={containerRef}>
      <input
        type="text"
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        value={userInput}
        className={`vx-autocomplete-search ${className || ''}`}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
        ref={inputRef}
        onFocus={() => setFocused(true)}
        autoFocus={autoFocus}
        onBlur={(e) => {
          if (onBlur) onBlur(e);
          setFocused(false);
        }}
      />
      {showSuggestions && (
        <PerfectScrollbar
          className={classnames('suggestions-list', {
            'open-up': openUp,
          })}
          ref={suggestionListRef}
          component="ul"
          options={{ wheelPropagation: false }}
        >
          {renderSuggestions()}
        </PerfectScrollbar>
      )}
    </div>
  );
});

AutocompleteField.displayName = 'AutocompleteField';

AutocompleteField.propTypes = {
  className: PropTypes.string,
  suggestions: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  clearInput: PropTypes.bool,
  externalClick: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  onSuggestionsShown: PropTypes.func,
  onSuggestionItemClick: PropTypes.func,
  value: PropTypes.string,
};

export default AutocompleteField;